import React from 'react';
import { Link } from 'react-router-dom';
import SEO from './SEO';
import { ArrowLeft, Shield, Eye, Lock, FileText, Users, Building2 } from 'lucide-react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-white">
      <SEO 
        title="Privacy Policy | Nix Spaces Workplace Monitoring"
        description="Privacy policy for Nix Spaces workplace phone monitoring. Learn how we protect employee data, what we collect during work hours, and our commitment to privacy."
        canonical="https://nixspaces.com/privacy"
      />

      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-12 w-auto mt-1" />
              <span className="text-2xl font-bold text-gray-900">Spaces</span>
            </div>
            <Link 
              to="/"
              className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Privacy Policy Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-center mb-12">
          <div className="bg-cyan-100 w-20 h-20 rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-lg">
            <Shield className="h-10 w-10 text-cyan-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-xl text-gray-600">Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>

        <div className="prose prose-lg max-w-none">
          <div className="bg-gray-50 rounded-2xl p-8 mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4 flex items-center">
              <Eye className="h-6 w-6 mr-3 text-cyan-600" />
              Overview
            </h2>
            <p className="text-gray-700 leading-relaxed">
              NixSpaces ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our workplace monitoring application and related services.
            </p>
          </div>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <FileText className="h-6 w-6 mr-3 text-cyan-600" />
              Information We Collect
            </h2>
            
            <div className="space-y-6">
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Device Usage Data</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• App usage patterns and screen time during work hours</li>
                  <li>• Device interaction timestamps and frequency</li>
                  <li>• Application activity and usage statistics</li>
                  <li>• Device performance metrics</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Account Information</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• Name and company affiliation</li>
                  <li>• Account preferences and settings</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Technical Data</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• Crash reports and error logs</li>
                </ul>
              </div>

              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Location Data (BLE Beacon Proximity)</h3>
                <p className="text-gray-700 mb-3">
                  NixSpaces uses Bluetooth Low Energy (BLE) beacons to detect proximity to specific devices at your workplace. The app continuously scans for nearby BLE beacons in the background to enable proximity-based features.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-3">
                  <p className="text-sm text-blue-900 font-semibold mb-2">Important: Location Permissions</p>
                  <p className="text-sm text-blue-800">
                    Location permissions are required by Android's security model for BLE scanning operations, even though the app does not track or use your actual geographic location. The app only detects the presence of specific BLE beacon devices within Bluetooth range at your workplace.
                  </p>
                </div>
                <ul className="text-gray-700 space-y-2">
                  <li>• <strong>What we collect:</strong> Proximity detection to BLE beacon devices (not geographic coordinates)</li>
                  <li>• <strong>What we do NOT collect:</strong> GPS location, geographic coordinates, or location data outside work sessions</li>
                  <li>• <strong>Purpose:</strong> Automatic timer activation and device communication when near workplace beacons</li>
                  <li>• <strong>Data handling:</strong> Location data is not sold, shared, or used for tracking outside of work proximity features</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <Building2 className="h-6 w-6 mr-3 text-cyan-600" />
              How We Use Your Information
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-cyan-900 mb-3">Workplace Safety</h3>
                <p className="text-cyan-800">
                  Monitor device usage to reduce distractions and improve workplace safety compliance.
                </p>
              </div>

              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-emerald-900 mb-3">Productivity Analytics</h3>
                <p className="text-emerald-800">
                  Generate reports to help managers understand team productivity patterns.
                </p>
              </div>

              <div className="bg-violet-50 border border-violet-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-violet-900 mb-3">Compliance Reporting</h3>
                <p className="text-violet-800">
                  Maintain detailed logs for regulatory compliance and safety audits.
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-amber-900 mb-3">Service Improvement</h3>
                <p className="text-amber-800">
                  Analyze usage patterns to improve our application and user experience.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Proximity-Based Features</h3>
                <p className="text-blue-800">
                  Use BLE beacon proximity detection to automatically activate timers and enable device communication when you are near workplace beacons. This proximity data is only used for work-related features and is not used for geographic location tracking.
                </p>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <Users className="h-6 w-6 mr-3 text-cyan-600" />
              Information Sharing
            </h2>
            
            <div className="bg-white border border-gray-200 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                We do not sell, trade, or otherwise transfer your personal information to third parties except in the following circumstances:
              </p>
              
              <ul className="text-gray-700 space-y-3">
                <li className="flex items-start">
                  <span className="bg-cyan-100 text-cyan-800 text-xs font-semibold px-2 py-1 rounded-full mr-3 mt-1">Employer</span>
                  <span>Your employer may access aggregated usage reports and compliance data as part of workplace safety initiatives.</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-emerald-100 text-emerald-800 text-xs font-semibold px-2 py-1 rounded-full mr-3 mt-1">Legal</span>
                  <span>We may disclose information when required by law or to protect our rights and safety.</span>
                </li>
                <li className="flex items-start">
                  <span className="bg-violet-100 text-violet-800 text-xs font-semibold px-2 py-1 rounded-full mr-3 mt-1">Service Providers</span>
                  <span>Trusted third-party service providers who assist in operating our platform (with strict confidentiality agreements).</span>
                </li>
              </ul>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6 flex items-center">
              <Lock className="h-6 w-6 mr-3 text-cyan-600" />
              Data Security
            </h2>
            
            <div className="bg-gradient-to-r from-cyan-50 to-blue-50 border border-cyan-200 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                We implement industry-standard security measures to protect your information:
              </p>
              
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Encryption</h4>
                  <p className="text-sm text-gray-600">All data is encrypted in transit and at rest using AES-256 encryption.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Access Controls</h4>
                  <p className="text-sm text-gray-600">Strict access controls and authentication protocols protect sensitive data.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Regular Audits</h4>
                  <p className="text-sm text-gray-600">We conduct regular security audits and vulnerability assessments.</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <h4 className="font-semibold text-gray-900 mb-2">Data Retention</h4>
                  <p className="text-sm text-gray-600">Data is retained only as long as necessary for business purposes.</p>
                </div>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Your Rights</h2>
            
            <div className="space-y-4">
              <div className="bg-white border border-gray-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">Access and Control</h3>
                <p className="text-gray-700 mb-3">
                  You have the right to:
                </p>
                <ul className="text-gray-700 space-y-2">
                  <li>• Access your personal information</li>
                  <li>• Request correction of inaccurate data</li>
                  <li>• Request deletion of your data (subject to legal requirements)</li>
                  <li>• Opt out of certain data collection</li>
                </ul>
              </div>
            </div>
          </section>

          <section className="mb-12">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Contact Us</h2>
            
            <div className="bg-gray-50 rounded-xl p-6">
              <p className="text-gray-700 mb-4">
                If you have any questions about this Privacy Policy or our data practices, please contact us:
              </p>
              
              <div className="space-y-2 text-gray-700">
                <p><strong>Email:</strong> privacy@nixspaces.com</p>
              </div>
            </div>
          </section>

          <div className="text-center pt-8 border-t border-gray-200">
            <p className="text-gray-600">
              This Privacy Policy is effective as of the date listed above and may be updated periodically. 
              We will notify you of any material changes to this policy.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img src="/image.png" alt="Nix Logo" className="h-8 w-auto filter invert" />
              <span className="text-xl font-bold">Spaces</span>
            </div>
            <div className="text-gray-400">
              © 2024 Nix Spaces. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
